import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {SelectInput, Text, TextInput, Button, Icon, PhoneInput} from '@/ui-kits';
import {useFormik} from 'formik';
import {useDispatch, useSelector} from 'react-redux';
import {
  authDataSelector,
  canChangePhoneSelector,
  canChangeEmailSelector,
  authSuccessStatus,
  authFailureStatus,
  allAppSpecializations,
  appCountries,
  isAuthLoading,
  specialistDashboardLoadingStatus,
  specialistDashboardSuccessStatus,
  specialistDashboardFailureStatus,
} from '@/services/redux/selectors';
import {
  updateSpecialistProfileRequest,
  profileOtpRequestRequest,
  profileOtpVerifyRequest,
  resetAuthMessage,
  getSpecialistProfileRequest,
} from '@/services/redux/slices/authSlice';
import {removeLicenseRequest, removeSignatureRequest, resetSpecialistDashboardMessage} from '@/services/redux/slices/specialistDashboardSlice';
import {OTPVerificationModal} from './OTPVerificationModal';
import {SuccessCodes, ErrorCodes, getErrorMessage} from '@/utils/getMessages';
import {Images, maxImageSize, NA} from '@/constants';
import {alphaNumericSpaceRegex} from '@/constants/regex';
import {SpecialistProfileEditValidationSchema} from '@/utils/schema';
import {FileViewerModal} from '../common/FileViewerModal';
import {useToast} from '@/hooks';
import {parsePhoneNumberFromString} from 'libphonenumber-js';

interface SpecialistProfileEditFormInterface {
  name: string;
  phone: string;
  email: string;
  location: number | null;
  designation: string;
  education: string;
  specialization: number | null;
  license_number: string;
  license_issue_year: string;
  years_of_experience: number | null;
  license_file: string | File | null;
  professional_experience: string;
  qualifications: string;
  awards_achievements: string[];
  areas_of_expertise: string[];
  photo: string | File | null;
  signature: string | File | null;
}

// Parse JSON strings if they exist - handles arrays and JSON strings
// Does NOT split on commas - treats comma-containing text as single entries
const parseArrayField = (field: any): string[] => {
  if (!field) return [];

  // Helper function to flatten and process items
  const processItem = (item: any): string[] => {
    // If item is a string that starts with [, try to parse it as JSON array
    if (typeof item === 'string' && item.startsWith('[')) {
      try {
        const parsed = JSON.parse(item);
        // If parsed is an array, recursively process each item
        if (Array.isArray(parsed)) {
          return parsed.flatMap(processItem);
        }
        // If parsed is a string, return it as is (don't split by comma)
        if (typeof parsed === 'string') {
          return [parsed];
        }
      } catch {
        // If parsing fails, return item as is
        return [item];
      }
    }

    // If item is an array, recursively process
    if (Array.isArray(item)) {
      return item.flatMap(processItem);
    }

    // Return as single string item (never split by comma)
    return [String(item)];
  };

  // If it's already an array, process each item
  if (Array.isArray(field)) {
    return field.flatMap(processItem);
  }

  // If it's a string, try to parse it as JSON
  if (typeof field === 'string') {
    try {
      const parsed = JSON.parse(field);
      // If parsed is an array, process it
      if (Array.isArray(parsed)) {
        return parsed.flatMap(processItem);
      }
      // If parsed is a string, return as single item
      return [parsed];
    } catch {
      // If parsing fails, return as single item
      return [field];
    }
  }

  return [];
};

const normalizeNumberField = (value: any): number | null => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const parseMobileNumber = (phone: string): string => {
  const parsed = parsePhoneNumberFromString(phone);
  const hasCountryCode = Boolean(parsed?.countryCallingCode);
  if (hasCountryCode) {
    return phone;
  }
  return `+91${phone}`; // return indian phone number
};

interface SpecialistProfileEditFormProps {
  onViewMode: (value: SpecialistProfileEditFormInterface) => void;
}

export const SpecialistProfileEditForm: React.FC<SpecialistProfileEditFormProps> = props => {
  const {onViewMode} = props;
  // ===========================
  // hook
  // ===========================
  const dispatch = useDispatch();
  const {showToast} = useToast();

  // ===========================
  // selectors
  // ===========================
  const authData = useSelector(authDataSelector);
  const specializations = useSelector(allAppSpecializations);
  const countries = useSelector(appCountries);
  const isLoading = useSelector(isAuthLoading);
  const canChangeEmail = useSelector(canChangeEmailSelector);
  const canChangePhone = useSelector(canChangePhoneSelector);
  const authSuccess = useSelector(authSuccessStatus) as SuccessCodes;
  const authFailure = useSelector(authFailureStatus) as ErrorCodes;
  const removeLicenseLoading = useSelector(specialistDashboardLoadingStatus);
  const removeLicenseSuccess = useSelector(specialistDashboardSuccessStatus) as SuccessCodes;
  const removeLicenseFailure = useSelector(specialistDashboardFailureStatus) as ErrorCodes;

  const specialistData = authData as any;

  // ===========================
  // formik
  // ===========================
  const initialValues = useMemo<SpecialistProfileEditFormInterface>(() => {
    return {
      name: authData?.username ?? '',
      phone: authData?.phone ? parseMobileNumber(authData.phone) : '',
      email: authData?.email_id ?? '',
      location: normalizeNumberField(specialistData?.location) ?? 76, // deafult to india (id 76) is location is null
      designation: specialistData?.designation ?? '',
      education: specialistData?.education ?? '',
      specialization: normalizeNumberField(specialistData?.specialization),
      license_number: specialistData?.license?.liscence_number ?? specialistData?.license?.license_number ?? specialistData?.license_number ?? '',
      license_issue_year: specialistData?.license?.license_year ?? specialistData?.license_issue_year ?? specialistData?.license_year ?? '',
      years_of_experience: normalizeNumberField(specialistData?.years_of_experience),
      license_file: specialistData?.license?.url ?? specialistData?.license_file ?? null,
      professional_experience: specialistData?.professional_experience ?? '',
      qualifications: specialistData?.qualifications ?? '',
      awards_achievements: (() => {
        const parsed = parseArrayField(specialistData?.awards_achievements);
        return parsed.length > 0 ? parsed : [''];
      })(),
      areas_of_expertise: (() => {
        const parsed = parseArrayField(specialistData?.areas_of_expertise);
        return parsed.length > 0 ? parsed : [''];
      })(),
      photo: authData?.photo_url ?? '',
      signature: specialistData?.specialist_signature ?? null,
    };
  }, [authData]);

  const {values, dirty, touched, errors, handleBlur, isValid, setFieldValue, setFieldTouched, validateForm, handleChange, handleSubmit} =
    useFormik<SpecialistProfileEditFormInterface>({
      initialValues,
      validationSchema: SpecialistProfileEditValidationSchema,
      onSubmit,
      validateOnChange: true,
      validateOnBlur: true,
    });

  // ===========================
  // states
  // ===========================
  const [emailVerificationModalOpen, setEmailVerificationModalOpen] = useState<boolean>(false);
  const [phoneVerificationModalOpen, setPhoneVerificationModalOpen] = useState<boolean>(false);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(true);
  const [isPhoneVerified, setIsPhoneVerified] = useState<boolean>(true);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [verifiedPhone, setVerifiedPhone] = useState<string | null>(null);
  const [licenseModalOpen, setLicenseModalOpen] = useState<boolean>(false);
  const [licenseImageError, setLicenseImageError] = useState(false);
  const [signatureImageError, setSignatureImageError] = useState(false);

  // ===========================
  // memo
  // ===========================
  const licensePreviewSource = useMemo(() => {
    if (!values.license_file) return null;
    if (typeof values.license_file === 'string') return values.license_file;
    return URL.createObjectURL(values.license_file);
  }, [values.license_file]);

  const isLicensePdf = useMemo(() => {
    if (!values.license_file) return false;
    if (typeof values.license_file === 'string') {
      return values.license_file.toLowerCase().endsWith('.pdf') || values.license_file.toLowerCase().includes('.pdf');
    }
    return values.license_file.type === 'application/pdf' || values.license_file.name?.toLowerCase().endsWith('.pdf');
  }, [values.license_file]);

  const signaturePreviewSource = useMemo(() => {
    if (!values.signature) return null;
    if (typeof values.signature === 'string') return values.signature;
    return URL.createObjectURL(values.signature);
  }, [values.signature]);

  // ===========================
  // refs
  // ===========================
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const licenseInputRef = React.useRef<HTMLInputElement>(null);
  const initalvaluesRef = useRef<SpecialistProfileEditFormInterface>(JSON.parse(JSON.stringify(initialValues)));
  const isSavingRef = useRef<boolean>(false);
  const isRemovingLicenseRef = useRef<boolean>(false);
  const isRemovingSignatureRef = useRef<boolean>(false);
  const previousSignatureRef = useRef<SpecialistProfileEditFormInterface['signature']>(null);

  // ===========================
  // functions
  // ===========================
  const isFieldEdited = useCallback(
    (key: keyof SpecialistProfileEditFormInterface) => {
      return values[key] !== initialValues[key];
    },
    [values, initialValues],
  );

  type DynamicListField = 'awards_achievements' | 'areas_of_expertise';

  function addListEntry(field: DynamicListField, index?: number) {
    const currentList = [...((values[field] as string[]) ?? [])];
    const insertionIndex = typeof index === 'number' ? index : currentList.length;
    currentList.splice(insertionIndex, 0, '');
    setFieldValue(field, currentList);
  }

  function removeListEntry(field: DynamicListField, index: number) {
    const currentList = [...((values[field] as string[]) ?? [])];
    // Don't allow deletion if it's the last item
    if (currentList.length <= 1) {
      return;
    }
    currentList.splice(index, 1);
    // Ensure at least one empty box remains
    if (currentList.length === 0) {
      currentList.push('');
    }
    setFieldValue(field, currentList);
  }

  function handleYearsOfExperienceChange(text: string) {
    if (!text) {
      setFieldValue('years_of_experience', null);
      return;
    }
    setFieldValue('years_of_experience', Number(text));
  }

  function getArrayFieldMeta(fieldErrors: any, fieldTouched: any, index: number) {
    const error = Array.isArray(fieldErrors) ? fieldErrors[index] : fieldErrors;
    const touchedValue = Array.isArray(fieldTouched) ? fieldTouched[index] : fieldTouched;
    return {
      error,
      touched: Boolean(touchedValue),
    };
  }

  function onSubmit(formValues: SpecialistProfileEditFormInterface) {
    isSavingRef.current = true;
    const formData = new FormData();
    // Fields that should always be included in the payload (all required fields except phone/license)
    const fieldsToAlwaysInclude: (keyof SpecialistProfileEditFormInterface)[] = [
      'name',
      'email',
      'location',
      'designation',
      'education',
      'specialization',
      'years_of_experience',
      'professional_experience',
      'qualifications',
      'awards_achievements',
      'areas_of_expertise',
      'photo',
      'signature',
    ];

    // Check if license was deleted (had a value initially, now null)
    const licenseWasDeleted =
      initialValues.license_file &&
      typeof initialValues.license_file === 'string' &&
      initialValues.license_file !== '' &&
      (formValues.license_file === null || formValues.license_file === '');

    // Check if signature was deleted (had a value initially, now null)
    const signatureWasDeleted =
      initialValues.signature &&
      typeof initialValues.signature === 'string' &&
      initialValues.signature !== '' &&
      (formValues.signature === null || formValues.signature === '');

    // Email - use the updated email from form values
    formData.append('email', formValues.email || '');

    // create object mapping
    Object.entries(formValues).forEach(([key, value]: [string, any]) => {
      // Skip email - already added above with current value from authData
      if (key === 'email') {
        return;
      }

      // Skip phone - not included in payload (handled via OTP)
      if (key === 'phone') {
        return;
      }

      // Always include required fields, or include if edited
      if (!fieldsToAlwaysInclude.includes(key as keyof SpecialistProfileEditFormInterface) && !isFieldEdited(key as keyof SpecialistProfileEditFormInterface)) {
        return;
      }

      // Map form field names to API field names
      let apiKey = key;
      if (key === 'years_of_experience') {
        apiKey = 'experience';
      } else if (key === 'qualifications') {
        apiKey = 'qualifications';
      } else if (key === 'awards_achievements') {
        // API has typo: 'awards_and_acheivements' instead of 'awards_and_achievements'
        apiKey = 'awards_and_acheivements';
      } else if (key === 'signature') {
        apiKey = 'specialist_signature';
      } else if (key === 'license_issue_year') {
        apiKey = 'license_year';
      }

      // Handle special fields (arrays need to be JSON stringified)

      if (apiKey === 'awards_and_acheivements' || apiKey === 'areas_of_expertise') {
        for (const item of value) {
          formData.append(apiKey, item);
        }
        return;
      }

      if (key === 'license_file') {
        if (value === null || value === '') {
          return;
        }
        // If it's a File, append it directly (new upload)
        if (value instanceof File) {
          formData.append('license', value);
          return;
        }
        return;
      }

      // Handle photo - if null or empty string, skip it (don't send empty string)
      if (key === 'photo') {
        if (value === null || value === '') {
          // Skip photo if not provided
          return;
        }
        // If it's a File, append it directly
        if (value instanceof File) {
          formData.append(apiKey, value);
          return;
        }
        // If it's a string (URL), skip it (existing photo, no change)
        return;
      }

      // Handle signature - if null or empty string, skip it (don't send empty string)
      if (key === 'signature') {
        if (value === null || value === '') {
          // Skip signature if not provided
          return;
        }
        // If it's a File, append it directly
        if (value instanceof File) {
          formData.append(apiKey, value);
          return;
        }
        // If it's a string (URL), skip it (existing signature, no change)
        return;
      }

      // Convert all other values to strings for FormData
      const stringValue = value !== null && value !== undefined ? String(value) : '';
      formData.append(apiKey, stringValue);
    });

    if (licenseWasDeleted && signatureWasDeleted) {
      isRemovingLicenseRef.current = true;
      isRemovingSignatureRef.current = true;
      previousSignatureRef.current = initialValues.signature;
      // Update profile first, then remove license and signature
      dispatch(updateSpecialistProfileRequest(formData));
      setTimeout(() => {
        dispatch(removeLicenseRequest());
        dispatch(removeSignatureRequest());
      }, 300);
    } else if (licenseWasDeleted) {
      isRemovingLicenseRef.current = true;
      // Update profile first, then remove license
      dispatch(updateSpecialistProfileRequest(formData));
      setTimeout(() => {
        dispatch(removeLicenseRequest());
      }, 300);
    } else if (signatureWasDeleted) {
      isRemovingSignatureRef.current = true;
      previousSignatureRef.current = initialValues.signature;
      // Update profile first, then remove signature
      dispatch(updateSpecialistProfileRequest(formData));
      setTimeout(() => {
        dispatch(removeSignatureRequest());
      }, 300);
    } else {
      dispatch(updateSpecialistProfileRequest(formData));
    }
  }

  function initiateMobileVerify() {
    dispatch(profileOtpRequestRequest({type: 'phone', value: values.phone}));
    // const isSamePhone = initalvaluesRef.current.phone === values.phone;
    // if (!isSamePhone) {
    // } else {
    //   setPhoneVerificationModalOpen(true);
    // }
  }

  function initiateEmailVerify() {
    dispatch(profileOtpRequestRequest({type: 'email', value: values.email}));
    // const isSameEmail = initalvaluesRef.current.email === values.email;
    // if (!isSameEmail) {
    // } else {
    //   setEmailVerificationModalOpen(true);
    // }
  }

  function handleMobileVerify(otp: string) {
    dispatch(profileOtpVerifyRequest({type: 'phone', otp}));
  }

  function handleEmailVerify(otp: string) {
    dispatch(profileOtpVerifyRequest({type: 'email', otp}));
  }

  // ===========================
  // side effects
  // ===========================
  // Only check verification status when email or phone values change
  useEffect(() => {
    // Check email verification
    if (verifiedEmail === null) {
      // Not verified yet, check if email is edited
      setIsEmailVerified(!isFieldEdited('email'));
    } else {
      // Email was verified, check if current email differs from verified email
      setIsEmailVerified(values.email === verifiedEmail);
    }
  }, [values.email, verifiedEmail]);

  useEffect(() => {
    // Check phone verification
    if (verifiedPhone === null) {
      // Not verified yet, check if phone is edited
      setIsPhoneVerified(!isFieldEdited('phone'));
    } else {
      // Phone was verified, check if current phone differs from verified phone
      setIsPhoneVerified(values.phone === verifiedPhone);
    }
  }, [values.phone, verifiedPhone]);

  useEffect(() => {
    if (authSuccess === 'S-10002' && !emailVerificationModalOpen) {
      setEmailVerificationModalOpen(true);
      // initalvaluesRef.current.email = values.email;
    }
    if (authSuccess === 'S-10031' && !phoneVerificationModalOpen) {
      setPhoneVerificationModalOpen(true);
      // initalvaluesRef.current.phone = values.phone;
    }
  }, [authSuccess, emailVerificationModalOpen, phoneVerificationModalOpen]);

  useEffect(() => {
    if (authSuccess === 'S-10024') {
      setIsPhoneVerified(true);
      setVerifiedPhone(values.phone); // Store the verified phone number
      setPhoneVerificationModalOpen(false);
      // initalvaluesRef.current.phone = values.phone;
    }
    if (authSuccess === 'S-10030') {
      setEmailVerificationModalOpen(false);
      setIsEmailVerified(true);
      setVerifiedEmail(values.email); // Store the verified email
      // initalvaluesRef.current.email = values.email;
    }
    if (authFailure && authFailure === 'E-10069') {
      setEmailVerificationModalOpen(false);
      setPhoneVerificationModalOpen(false);
    }
  }, [authSuccess, authFailure, values.phone, values.email]);

  // Handle license removal success/failure
  useEffect(() => {
    if (removeLicenseFailure && isRemovingLicenseRef.current) {
      showToast(getErrorMessage(removeLicenseFailure), 'failure');
      dispatch(resetSpecialistDashboardMessage());
      isSavingRef.current = false;
      isRemovingLicenseRef.current = false;
    }
    return () => {
      if (removeLicenseFailure && isRemovingLicenseRef.current) {
        dispatch(resetSpecialistDashboardMessage());
      }
    };
  }, [removeLicenseFailure, showToast, dispatch]);

  // Handle license removal success - only show toast if not saving (to avoid form reset)
  useEffect(() => {
    if (removeLicenseSuccess && String(removeLicenseSuccess).startsWith('S-') && isRemovingLicenseRef.current) {
      if (!isSavingRef.current) {
        setFieldValue('license_file', null);
      }
      dispatch(resetSpecialistDashboardMessage());
      isSavingRef.current = false;
      isRemovingLicenseRef.current = false;
    }
  }, [removeLicenseSuccess, dispatch, showToast, setFieldValue]);

  // Handle signature removal success/failure
  useEffect(() => {
    if (removeLicenseFailure && isRemovingSignatureRef.current) {
      showToast(getErrorMessage(removeLicenseFailure), 'failure');
      setFieldValue('signature', previousSignatureRef.current ?? null);
      dispatch(resetSpecialistDashboardMessage());
      isRemovingSignatureRef.current = false;
    }
    return () => {
      if (removeLicenseFailure && isRemovingSignatureRef.current) {
        dispatch(resetSpecialistDashboardMessage());
      }
    };
  }, [removeLicenseFailure, showToast, dispatch, setFieldValue]);

  useEffect(() => {
    if (removeLicenseSuccess && String(removeLicenseSuccess).startsWith('S-') && isRemovingSignatureRef.current) {
      setFieldValue('signature', null);
      setSignatureImageError(false);
      dispatch(resetSpecialistDashboardMessage());
      isRemovingSignatureRef.current = false;
    }
  }, [removeLicenseSuccess, dispatch, setFieldValue]);

  // useEffect(() => {
  //   initalvaluesRef.current = JSON.parse(JSON.stringify(initialValues));
  // }, [initialValues]);

  return (
    <>
      {/* Main Card Container */}
      <div className="bg-white rounded-xl shadow-[0px_4px_41.6px_-13px_rgba(0,0,0,0.15)] p-3 sm:p-6 space-y-4 sm:space-y-6 border border-border">
        {/* Complete Your Profile Header */}
        <div className="mb-2">
          <Text variant="free" className="text-xl sm:text-3xl text-ui_blue font-FigtreeBold">
            Complete Your Profile
          </Text>
          <Text variant="free" className="text-ui_input_border_dark text-sm sm:text-lg ml-1 font-FigtreeMeduim">
            Fill in your professional details to become visible to patients!
          </Text>
        </div>

        {/* Professional Photo Section Card */}
        <div className="bg-gray-50 rounded-lg px-3 sm:px-6 pt-4 sm:pt-6 pb-6 sm:pb-10 border border-border">
          <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-2xl">
            Professional Photo
          </Text>
          <Text variant="free" className="text-ui_input_border_dark text-md sm:text-md mb-8 sm:mb-10 font-FigtreeMeduim">
            This image will appear on your public profile once approved.
          </Text>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-0 lg:gap-2 mt-8 sm:mt-8">
            <div className="flex flex-col items-center 2xl:items-end 2xl:pr-4">
              <div className="relative w-full max-w-[272px] aspect-square sm:h-[272px] rounded-lg overflow-hidden">
                <img src={Images.specialistSampleReferenceBg} alt="Sample reference background" className="absolute inset-0 w-full h-full object-cover" />
                <img src={Images.specialistSampleReferenceLady} alt="Sample reference" className="relative w-full h-full object-contain" />
              </div>
              <Text variant="free" className="font-FigtreeSemiBold text-ui_input_border_dark text-sm sm:text-xl mt-2 text-center max-w-[272px] w-full">
                Sample Reference
              </Text>
            </div>
            <div className="flex flex-col items-center 2xl:items-start 2xl:pl-4 w-full mt-10 md:mt-0">
              {values.photo ? (
                <>
                  <div className="relative w-full max-w-[272px] aspect-square sm:h-[272px] rounded-lg sm:rounded-lg bg-profile_bg_light_blue flex-shrink-0 flex items-end justify-center overflow-visible border border-profile_border_light_blue 2xl:mx-auto">
                    {typeof values.photo === 'string' ? (
                      <img src={values.photo} alt="Uploaded" className="h-[270px] sm:h-[300px] w-auto object-contain object-bottom -mt-4 sm:-mt-8" />
                    ) : (
                      <img
                        src={URL.createObjectURL(values.photo)}
                        alt="Uploaded"
                        className="h-[300px] sm:h-[300px] w-auto object-contain object-bottom -mt-4 sm:-mt-8"
                      />
                    )}
                  </div>
                  <label htmlFor="photo-upload" className="mt-3 w-full flex justify-center 2xl:justify-center">
                    <input
                      id="photo-upload"
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > maxImageSize) {
                            // Handle error - file too large
                            showToast(getErrorMessage('E-10013'), 'failure');
                            return;
                          }
                          setFieldValue('photo', file);
                        }
                      }}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => document.getElementById('photo-upload')?.click()}
                      className="max-w-[272px] px-4 py-1 border border-ui_blue rounded-lg bg-white hover:bg-ui_blue_light/10 font-FigtreeSemiBold text-sm sm:text-lg shadow-sm transition-colors">
                      <span className="gradient-text">Upload New</span>
                    </button>
                  </label>
                </>
              ) : (
                <label
                  htmlFor="photo-upload"
                  className="w-full max-w-[272px] mx-auto aspect-square sm:h-[272px] border border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                  <input
                    id="photo-upload"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          // Handle error - file too large
                          showToast('Please upload file under 5 MB', 'failure');
                          return;
                        }
                        setFieldValue('photo', file);
                      }
                    }}
                    className="hidden"
                  />
                  <div className="mb-2 sm:mb-3 text-upload_text_blue">
                    <Icon type="download_icon" className="w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                  <Text variant="free" className="text-upload_text_blue font-FigtreeSemiBold text-sm sm:text-lg sm:mb-2">
                    Click to upload photo
                  </Text>
                  <Text variant="free" className="text-upload_text_blue font-FigtreeSemiBold text-sm sm:text-lg mb-4">
                    JPG or PNG, max 5MB
                  </Text>
                  <Text variant="free" className="text-ui_input_border_dark font-FigtreeRegular text-xs sm:text-base text-center px-2">
                    Upload a half-body professional image with a plain or blue background.
                  </Text>
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information Section Card */}
        <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
          <div className="flex items-center gap-2 mb-1">
            <Icon type="email" className="w-4 h-4 sm:w-5 sm:h-5" />
            <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-2xl ml-1 sm:ml-2">
              Contact Information
            </Text>
          </div>
          <Text variant="free" className="ml-6 sm:ml-9 text-ui_input_border_dark text-md sm:text-md mb-4 sm:mb-6 font-FigtreeMeduim">
            Your contact details for communication
          </Text>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 sm:gap-x-20 gap-y-4 sm:gap-y-6">
            <TextInput
              label="Full Name"
              required
              value={values.name}
              error={errors.name}
              touched={touched.name}
              onChangeText={handleChange('name')}
              onBlur={handleBlur('name')}
              placeholder="Enter your full name"
              max={50}
              preventLeadingSpace
              preventTrailingSpace
              labelClassName="!font-FigtreeSemiBold text-sm sm:text-xl"
              wrapperClassName="bg-white"
            />
            <PhoneInput
              label="Phone Number"
              required
              value={values.phone}
              error={errors.phone}
              disabled={Boolean(!canChangePhone)}
              touched={touched.phone}
              onChange={handleChange('phone')}
              onBlur={handleBlur('phone')}
              placeholder="Enter your phone"
              labelClassName="!font-FigtreeSemiBold text-sm sm:text-xl"
              wrapperClassName="bg-white"
              button={{
                text: 'Verify',
                onClick: initiateMobileVerify,
                // making sure the new phone enter is also valid before proceeding with next steps
                disabled: !!errors?.phone || isPhoneVerified,
              }}
            />
            <TextInput
              label="Email Address"
              required
              value={values.email}
              error={errors.email}
              touched={touched.email}
              disabled={Boolean(!canChangeEmail)}
              onChangeText={handleChange('email')}
              onBlur={handleBlur('email')}
              placeholder="Enter your email"
              preventLeadingSpace
              preventTrailingSpace
              labelClassName="!font-FigtreeSemiBold text-sm sm:text-xl"
              wrapperClassName="bg-white"
              button={{
                text: 'Verify',
                onClick: initiateEmailVerify,
                disabled: !!errors?.email || isEmailVerified,
              }}
            />
            {specialistData?.is_hospital_specialist ? (
              <div className='flex flex-col'>
                <Text className='text-ui_blue !font-FigtreeSemiBold'>Location: </Text>
                <Text className='text-ui_blue my-auto !font-FigtreeSemiBold !text-[17px]'>{`${specialistData?.hospital_name ?? NA}, ${specialistData?.hospital_address ?? NA}`}</Text>
              </div>
            ) : (
              <SelectInput
                label="Location"
                required
                value={values.location}
                error={errors.location}
                touched={touched.location}
                options={countries || []}
                onChange={({id}) => setFieldValue('location', id)}
                onBlur={handleBlur('location')}
                placeholder="Select location"
                wrapperClassName="max-w-full bg-white"
                labelClassName="!font-FigtreeSemiBold text-sm sm:text-xl"
              />
            )}
          </div>
        </div>

        {/* Professional Information Section Card */}
        <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
          <div className="flex items-center gap-2 mb-1">
            <Icon type="stethoscope2" className="w-4 h-4 sm:w-5 sm:h-5" />
            <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-2xl ml-1 sm:ml-2">
              Professional Information
            </Text>
          </div>
          <Text variant="free" className="ml-6 sm:ml-9 text-ui_input_border_dark text-md sm:text-md mb-4 sm:mb-6 font-FigtreeMeduim">
            Your medical qualification and expertise
          </Text>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 sm:gap-x-20 gap-y-4 sm:gap-y-6">
            <div className="flex flex-col gap-4 sm:gap-6">
              <TextInput
                label="Current Designation"
                required
                value={values.designation}
                error={errors.designation}
                touched={touched.designation}
                onChangeText={handleChange('designation')}
                onBlur={handleBlur('designation')}
                placeholder="Enter your designation"
                max={50}
                preventLeadingSpace
                preventTrailingSpace
                labelClassName="!font-FigtreeSemiBold text-sm sm:text-xl"
                wrapperClassName="bg-white"
              />
              <SelectInput
                label="Specialization"
                required
                value={values.specialization}
                error={errors.specialization}
                touched={touched.specialization}
                options={specializations || []}
                onChange={({id}) => setFieldValue('specialization', id)}
                onBlur={handleBlur('specialization')}
                placeholder="Select specialization"
                wrapperClassName="max-w-full bg-white"
                labelClassName="!font-FigtreeSemiBold text-sm sm:text-xl"
                showSearch
              />
              <TextInput
                label="Years of Experience"
                required
                type="numeric"
                value={values.years_of_experience !== null ? values.years_of_experience.toString() : ''}
                error={errors.years_of_experience}
                touched={touched.years_of_experience}
                onChangeText={handleYearsOfExperienceChange}
                onBlur={handleBlur('years_of_experience')}
                placeholder="Enter years of experience"
                max={2}
                labelClassName="!font-FigtreeSemiBold text-sm sm:text-xl"
                wrapperClassName="bg-white"
              />
              <TextInput
                label="Education"
                required
                value={values.education}
                error={errors.education}
                touched={touched.education}
                onChangeText={handleChange('education')}
                onBlur={handleBlur('education')}
                placeholder="Enter your education"
                preventLeadingSpace
                preventTrailingSpace
                labelClassName="!font-FigtreeSemiBold text-sm sm:text-xl"
                wrapperClassName="bg-white"
              />
            </div>
            <div className="flex flex-col gap-4 sm:gap-6">
              <TextInput
                label="License Number"
                required
                value={values.license_number}
                error={errors.license_number}
                touched={touched.license_number}
                onChangeText={handleChange('license_number')}
                onBlur={handleBlur('license_number')}
                placeholder="Enter license number"
                preventLeadingSpace
                preventTrailingSpace
                labelClassName="!font-FigtreeSemiBold text-sm sm:text-xl"
                wrapperClassName="bg-white"
              />
              <TextInput
                label="License Issue Year"
                required
                value={values.license_issue_year}
                error={errors.license_issue_year}
                touched={touched.license_issue_year}
                onChangeText={handleChange('license_issue_year')}
                onBlur={handleBlur('license_issue_year')}
                placeholder="Enter license issue year (e.g., 2016)"
                labelClassName="!font-FigtreeSemiBold text-sm sm:text-xl"
                preventLeadingSpace
                preventTrailingSpace
                wrapperClassName="bg-white"
              />
              <div>
                <Text variant="body" className="text-ui_blue mb-2 !font-FigtreeSemiBold text-sm sm:text-xl">
                  License
                </Text>
                {licensePreviewSource ? (
                  <div className="relative w-full max-w-[200px] h-[120px] sm:h-[150px] border border-dashed border-border rounded-lg overflow-hidden bg-white">
                    {!isLicensePdf && !licenseImageError ? (
                      <img src={licensePreviewSource} alt="License preview" className="w-full h-full object-cover" onError={() => setLicenseImageError(true)} />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gray-50">
                        <Icon type="file" className="w-10 h-10" />
                        <Text variant="free" className="text-sm sm:text-base text-center px-4 text-ui_blue">
                          {isLicensePdf ? 'PDF Document' : 'Preview not available'}
                        </Text>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/45 pointer-events-none" />
                    <button
                      type="button"
                      onClick={() => setLicenseModalOpen(true)}
                      className="absolute left-1/2 -translate-x-1/2 bottom-14 h-6 px-4 border-white border-2 rounded-full text-white font-FigtreeSemiBold hover:opacity-80 transition-opacity z-10">
                      View
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        // Only update local form state, don't call API immediately
                        // License deletion will be handled when Save button is clicked
                        setFieldValue('license_file', null);
                      }}
                      className="absolute top-2 right-2 bg-white rounded-full p-1.5 transition-opacity shadow-md flex items-center justify-center z-10 hover:opacity-80">
                      <Icon type="trash_red" className="size-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="license-upload"
                    className="w-full max-w-[200px] h-[120px] sm:h-[150px] border border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                    <input
                      id="license-upload"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            showToast('Please upload file under 5 MB', 'failure');
                            return;
                          }
                          setFieldValue('license_file', file);
                        }
                      }}
                      className="hidden"
                    />
                    <div className="mb-2 sm:mb-3 text-upload_text_blue">
                      <Icon type="download_icon" className="w-5 h-5 sm:w-8 sm:h-8" />
                    </div>
                    <Text variant="free" className="text-upload_text_blue font-FigtreeSemiBold text-xs sm:text-sm sm:mb-2 mt-2">
                      Click to upload photo
                    </Text>
                    <Text variant="free" className="text-upload_text_blue font-FigtreeSemiBold text-xs sm:text-sm mb-4">
                      JPG or PNG, max 5MB
                    </Text>
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Professional Experience Section Card */}
        <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <Icon type="business_center_logo" className="w-4 h-4 sm:w-5 sm:h-5" />
            <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-2xl ml-1 sm:ml-2">
              Professional Experience
            </Text>
          </div>
          <TextInput
            label="Enter your experience"
            required
            value={values.professional_experience}
            error={errors.professional_experience}
            touched={touched.professional_experience}
            multiline
            onChangeText={handleChange('professional_experience')}
            onBlur={handleBlur('professional_experience')}
            placeholder="Enter your professional experience"
            max={1000}
            preventLeadingSpace
            preventTrailingSpace
            wrapperClassName="bg-white"
            labelClassName="!font-FigtreeSemiBold text-sm sm:text-xl"
          />
        </div>

        {/* Qualifications Section Card */}
        <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <Icon type="graduation_icon" className="w-4 h-4 sm:w-5 sm:h-5" />
            <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-2xl ml-1 sm:ml-2">
              Qualifications
            </Text>
          </div>
          <TextInput
            label="Enter your educational background"
            required
            value={values.qualifications}
            error={errors.qualifications}
            touched={touched.qualifications}
            multiline
            onChangeText={handleChange('qualifications')}
            onBlur={handleBlur('qualifications')}
            placeholder="Enter your qualifications"
            max={600}
            preventLeadingSpace
            preventTrailingSpace
            wrapperClassName="bg-white"
            labelClassName="!font-FigtreeSemiBold text-sm sm:text-xl"
          />
        </div>

        {/* Awards & Achievements and Areas of Expertise - Side by Side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 items-start">
          {/* Awards & Achievements Section Card */}
          <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <Icon type="awards_logo" className="w-4 h-4 sm:w-5 sm:h-5" />
              <Text variant="title" className="text-ui_blue font-FigtreeSemiBold text-base sm:text-lg ml-2 2xl:whitespace-nowrap">
                Awards & Achievements <span className="text-rejection">*</span>
              </Text>
            </div>
            <div className="space-y-3">
              {values.awards_achievements.map((award, index) => {
                const {error: awardError, touched: awardTouched} = getArrayFieldMeta(errors.awards_achievements, touched.awards_achievements, index);
                return (
                  <div key={`award-${index}`} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <TextInput
                        value={award}
                        error={awardError}
                        touched={awardTouched}
                        onChangeText={value => {
                          const newAwards = [...values.awards_achievements];
                          newAwards[index] = value;
                          setFieldValue('awards_achievements', newAwards);
                        }}
                        onBlur={handleBlur(`awards_achievements[${index}]`)}
                        placeholder="Enter award or achievement"
                        className="flex-1"
                        wrapperClassName="bg-white"
                        max={250}
                        preventLeadingSpace
                        preventTrailingSpace
                      />
                      {values.awards_achievements.length > 1 && (
                        <button
                          type="button"
                          aria-label="Remove award"
                          onClick={() => removeListEntry('awards_achievements', index)}
                          className="p-2 rounded-full hover:bg-ui_blue_light/20 transition-colors mt-1">
                          <Icon type="trash_red" className="w-6 h-6" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => addListEntry('awards_achievements')}
                  className="text-ui_blue font-FigtreeSemiBold text-sm sm:text-md mt-4 hover:opacity-80 transition-opacity">
                  + Add More
                </button>
              </div>
            </div>
          </div>

          {/* Areas of Expertise Section Card */}
          <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <Icon type="expertise_logo" className="w-4 h-4 sm:w-5 sm:h-5" />
              <Text variant="title" className="text-ui_blue font-FigtreeSemiBold text-base sm:text-lg ml-2">
                Areas of Expertise <span className="text-rejection">*</span>
              </Text>
            </div>
            <div className="space-y-3">
              {values.areas_of_expertise.map((expertise, index) => {
                const {error: expertiseError, touched: expertiseTouched} = getArrayFieldMeta(errors.areas_of_expertise, touched.areas_of_expertise, index);
                return (
                  <div key={`expertise-${index}`} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <TextInput
                        value={expertise}
                        error={expertiseError}
                        touched={expertiseTouched}
                        onChangeText={value => {
                          const newExpertise = [...values.areas_of_expertise];
                          newExpertise[index] = value;
                          setFieldValue('areas_of_expertise', newExpertise);
                        }}
                        onBlur={handleBlur(`areas_of_expertise[${index}]`)}
                        placeholder="Enter area of expertise"
                        className="flex-1"
                        wrapperClassName="bg-white"
                        max={250}
                        preventLeadingSpace
                        preventTrailingSpace
                      />
                      {values.areas_of_expertise.length > 1 && (
                        <button
                          type="button"
                          aria-label="Remove area of expertise"
                          onClick={() => removeListEntry('areas_of_expertise', index)}
                          className="p-2 rounded-full hover:bg-ui_blue_light/20 transition-colors mt-1">
                          <Icon type="trash_red" className="w-6 h-6" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => addListEntry('areas_of_expertise')}
                  className="text-ui_blue font-FigtreeSemiBold text-sm sm:text-md mt-4 hover:opacity-80 transition-opacity">
                  + Add More
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Add Signature Section Card */}
        <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <Icon type="signature" className="w-4 h-4 sm:w-5 sm:h-5" />
            <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-2xl ml-1 sm:ml-2">
              Add Signature <span className="text-rejection">*</span>
            </Text>
          </div>
          {signaturePreviewSource ? (
            <div className="flex flex-col ml-8 sm:ml-9">
              <div className="relative w-full h-[200px] sm:h-[250px] border border-dashed border-border rounded-lg overflow-hidden bg-white">
                {!signatureImageError ? (
                  <img
                    src={signaturePreviewSource}
                    alt="Signature preview"
                    className="w-full h-full object-contain"
                    onError={() => setSignatureImageError(true)}
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gray-50">
                    <Icon type="file" className="w-10 h-10" />
                    <Text variant="free" className="text-sm sm:text-base text-center px-4 text-ui_blue">
                      Preview not available
                    </Text>
                  </div>
                )}
                <button
                  type="button"
                  onClick={async () => {
                    previousSignatureRef.current = values.signature;
                    setFieldValue('signature', null);
                    setFieldTouched('signature', true); // Mark as touched to trigger validation
                    setSignatureImageError(false);
                    // Manually trigger validation to ensure isValid updates immediately
                    await validateForm();
                  }}
                  className="absolute top-2 right-2 bg-white rounded-full p-1.5 transition-opacity shadow-md flex items-center justify-center z-10 hover:opacity-80">
                  <Icon type="trash_red" className="size-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="ml-8 sm:ml-9 min-w-0 overflow-hidden">
              <label
                htmlFor="signature-upload"
                className="w-full h-[200px] sm:h-[250px] border border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                <input
                  id="signature-upload"
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 2 * 1024 * 1024) {
                        showToast('Please upload file under 2 MB', 'failure');
                        return;
                      }
                      setSignatureImageError(false);
                      setFieldValue('signature', file);
                    }
                  }}
                  className="hidden"
                />
                <div className="mb-2 sm:mb-3 text-upload_text_blue">
                  <Icon type="download_icon" className="w-6 h-6 sm:w-8 sm:h-8" />
                </div>
                <Text variant="free" className="text-upload_text_blue font-FigtreeSemiBold text-sm sm:text-lg sm:mb-2">
                  Click to upload signature <span className="text-rejection">*</span>
                </Text>
                <Text variant="free" className="text-upload_text_blue font-FigtreeSemiBold text-sm sm:text-lg mb-2">
                  JPG or PNG, max 2MB
                </Text>
                <Text variant="free" className="text-ui_input_border_dark font-FigtreeRegular text-xs sm:text-base text-center px-2">
                  Use a clear signature on a plain background
                </Text>
              </label>
            </div>
          )}
          {errors.signature && touched.signature && (
            <Text variant="free" className="text-rejection text-xs sm:text-sm mt-2 ml-8 sm:ml-9">
              {errors.signature}
            </Text>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-center pt-4">
          <Button
            title="Save"
            variant="primary"
            size="lg"
            onClick={() => handleSubmit()}
            disabled={!dirty || !isValid || !values.signature}
            className="min-w-[160px] sm:min-w-[200px]"
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* OTP Modals */}
      <OTPVerificationModal
        isOpen={phoneVerificationModalOpen}
        onClose={() => setPhoneVerificationModalOpen(false)}
        onVerify={handleMobileVerify}
        type="phone"
        phoneNumber={values.phone}
      />
      <OTPVerificationModal
        isOpen={emailVerificationModalOpen}
        onClose={() => setEmailVerificationModalOpen(false)}
        onVerify={handleEmailVerify}
        type="email"
        email={values.email}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) setFieldValue('photo', file);
        }}
      />
      <input
        ref={licenseInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) setFieldValue('license_file', file);
        }}
      />

      {licensePreviewSource && (
        <FileViewerModal
          file={{
            id: 0,
            name: 'License',
            url: licensePreviewSource,
          }}
          isOpen={licenseModalOpen}
          onClose={() => setLicenseModalOpen(false)}
          showDownloadButton
          isSecure={false}
          file_type={isLicensePdf ? 'pdf' : 'image'}
        />
      )}
    </>
  );
};
